import { AdminUserResponse, OrganisationWithRole, User } from '@avanzu/types';
import BusinessTwoTone from '@mui/icons-material/BusinessTwoTone';
import LoginTwoTone from '@mui/icons-material/LoginTwoTone';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ActivityList from '../../../components/ActivityList';
import BreadCrumb from '../../../components/BreadCrumb';
import OrganisationChip from '../../../components/OrganisationChip';
import getRoleColor from '../../../helpers/getRoleColor';
import getRoleLabel from '../../../helpers/getRoleLabel';
import getStatusColor from '../../../helpers/getStatusColor';
import { apiClient } from '../../../services/apiClient';
import formatDate, { DateFormatOptions } from '../../../utils/formatDate';
import { getSubscriptionStatusColor } from '../../../utils/getSubscriptionStatusColor';

export default function UserView() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [organisations, setOrganisations] = useState<OrganisationWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      fetchUser();
    }
  }, [userId]);

  const fetchUser = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/users/${userId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data: AdminUserResponse = await response.json();
      setUser(data.user);
      setOrganisations(data.organisations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  };

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const StatCard = ({
    icon,
    label,
    value,
    color = 'primary.main',
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    color?: string;
  }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !user) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <BreadCrumb />
          <Typography variant="h5" color="error">
            {error || 'User not found'}
          </Typography>
        </Box>
      </Container>
    );
  }

  const userName =
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;

  return (
    <Container maxWidth="xl">
      <BreadCrumb customLabel={userName} />

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={user.avatarUrl || undefined}
            sx={{ width: 64, height: 64, fontSize: '1.5rem' }}
          >
            {user.firstName && user.lastName
              ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
              : user.email[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {userName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={user.status}
          sx={{ height: 32, fontSize: '0.875rem', backgroundColor: getStatusColor(user.status) }}
        />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <StatCard icon={<BusinessTwoTone />} label="Organisations" value={organisations.length} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard
            icon={<LoginTwoTone />}
            label="Last Login"
            value={user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
          />
        </Grid>
      </Grid>

      {/* Main Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            User Information
          </Typography>
          <Grid container spacing={4}>
            {/* Personal Information */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Personal Information
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="First Name" value={user.firstName} />
                <InfoItem label="Last Name" value={user.lastName} />
                <InfoItem label="Email" value={user.email} />
                <InfoItem
                  label="Email Verified"
                  value={
                    <Chip
                      label={user.emailVerified ? 'Verified' : 'Not Verified'}
                      color={user.emailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  }
                />
              </Stack>
            </Grid>

            {/* Account Information */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Account Information
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="Created" value={formatDate(user.createdAt)} />
                <InfoItem
                  label="Last Login"
                  value={user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                />
                <InfoItem
                  label="2FA Enabled"
                  value={
                    <Chip
                      label={user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      color={user.twoFactorEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  }
                />
                <InfoItem
                  label="Admin"
                  value={
                    <Chip
                      label={user.isAdmin ? 'Yes' : 'No'}
                      color={user.isAdmin ? 'primary' : 'default'}
                      size="small"
                    />
                  }
                />
              </Stack>
            </Grid>

            {/* OAuth Information */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Authentication
              </Typography>
              <Stack spacing={2}>
                <InfoItem
                  label="Providers"
                  value={
                    user.providers.length > 0 ? (
                      <Stack direction="row" spacing={1}>
                        {user.providers.map((provider) => (
                          <Chip key={provider} label={provider} size="small" />
                        ))}
                      </Stack>
                    ) : (
                      'None'
                    )
                  }
                />
                <InfoItem label="Google ID" value={user.googleId} />
                <InfoItem label="Xero ID" value={user.xeroId} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Organisations Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Organisations ({organisations.length})
          </Typography>
          {organisations.length === 0 ? (
            <Typography color="text.secondary">No organisations</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Legal Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Subscription Status</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organisations.map((org) => (
                    <TableRow
                      key={org.id}
                      onClick={() => navigate(`/app/organisations/${org.id}`)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell>
                        <OrganisationChip organisation={org} />
                      </TableCell>
                      <TableCell>{org.legalName}</TableCell>
                      <TableCell>
                        <Chip label={getRoleLabel(org.role)} size="small" color={getRoleColor(org.role)} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={org.subscriptionStatus}
                          color={getSubscriptionStatusColor(org.subscriptionStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(org.createdAt, DateFormatOptions.DATEONLY)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Activities Card */}
      {userId && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Recent Activities
            </Typography>
            <ActivityList
              endpoint={`/admin/activities/user/${userId}`}
              showUsers={false}
              showOrganisations={true}
            />
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
